import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { hrEmployeesUrl, hrEmployeeByIdUrl, attendanceDevicesUrl, branchesUrl } from '../../BaseURL';
import Modal from '../../components/Modal';
import { fmtDate } from '../../utils/format';
import Swal from 'sweetalert2';
import { toastSuccess, showAxiosError, swalDangerConfirm } from '../../utils/swal';

const EMPTY = {
  employee_no: '', full_name: '', gender: '', date_of_birth: '',
  national_id: '', phone: '', email: '', address: '',
  department: '', position: '', employment_type: 'Full-time',
  hire_date: '', end_date: '', branch_id: '',
  device_user_id: '', device_serial: '',
};

const EMP_TYPES = ['Full-time', 'Part-time', 'Contract', 'Casual'];

const HREmployees = () => {
  const [me] = useState(() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; } });
  const canManage = me?.role === 'super_admin' || me?.role === 'branch_manager';

  const [employees, setEmployees]   = useState([]);
  const [devices,   setDevices]     = useState([]);
  const [branches,  setBranches]    = useState([]);
  const [loading,   setLoading]     = useState(true);
  const [open,      setOpen]        = useState(false);
  const [editId,    setEditId]      = useState(null);
  const [form,      setForm]        = useState(EMPTY);
  const [saving,    setSaving]      = useState(false);
  const [search,    setSearch]      = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('1');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeFilter !== '') params.active = activeFilter;
      if (deptFilter)          params.department = deptFilter;
      const [emp, dev, br] = await Promise.allSettled([
        axios.get(hrEmployeesUrl, { params }),
        axios.get(attendanceDevicesUrl),
        axios.get(branchesUrl),
      ]);
      if (emp.status === 'fulfilled') setEmployees(emp.value.data?.results || []);
      if (dev.status === 'fulfilled') setDevices(dev.value.data?.results   || []);
      if (br.status  === 'fulfilled') setBranches(br.value.data?.results   || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, [activeFilter, deptFilter]);

  const openNew = () => {
    setEditId(null);
    setForm({ ...EMPTY, branch_id: me?.branch_id || '' });
    setOpen(true);
  };

  const openEdit = (emp) => {
    setEditId(emp.id);
    setForm({
      employee_no:     emp.employee_no     || '',
      full_name:       emp.full_name       || '',
      gender:          emp.gender          || '',
      date_of_birth:   emp.date_of_birth   ? emp.date_of_birth.slice(0, 10) : '',
      national_id:     emp.national_id     || '',
      phone:           emp.phone           || '',
      email:           emp.email           || '',
      address:         emp.address         || '',
      department:      emp.department      || '',
      position:        emp.position        || '',
      employment_type: emp.employment_type || 'Full-time',
      hire_date:       emp.hire_date       ? emp.hire_date.slice(0, 10)  : '',
      end_date:        emp.end_date        ? emp.end_date.slice(0, 10)   : '',
      branch_id:       emp.branch_id       || '',
      device_user_id:  emp.device_user_id  || '',
      device_serial:   emp.device_serial   || '',
    });
    setOpen(true);
  };

  const f = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const save = async () => {
    if (!form.full_name.trim()) {
      await Swal.fire('Missing field', 'Full name is required.', 'warning');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        branch_id:      form.branch_id      ? Number(form.branch_id)      : null,
        device_user_id: form.device_user_id ? String(form.device_user_id) : null,
        device_serial:  form.device_serial  || null,
        date_of_birth:  form.date_of_birth  || null,
        hire_date:      form.hire_date      || null,
        end_date:       form.end_date       || null,
        employee_no:    form.employee_no    || null,
        gender:         form.gender         || null,
        national_id:    form.national_id    || null,
      };
      if (editId) {
        await axios.put(hrEmployeeByIdUrl(editId), payload);
        toastSuccess('Employee updated');
      } else {
        await axios.post(hrEmployeesUrl, payload);
        toastSuccess('Employee registered');
      }
      setOpen(false);
      load();
    } catch (err) { showAxiosError(err); }
    finally { setSaving(false); }
  };

  const deactivate = async (emp) => {
    const ok = await swalDangerConfirm(
      `Deactivate ${emp.full_name}?`,
      'Their attendance history is kept. You can reactivate later by clicking Reactivate.',
      { confirmButtonText: 'Deactivate' }
    );
    if (!ok) return;
    try {
      await axios.delete(hrEmployeeByIdUrl(emp.id));
      toastSuccess('Employee deactivated');
      load();
    } catch (err) { showAxiosError(err); }
  };

  const reactivate = async (emp) => {
    try {
      await axios.put(hrEmployeeByIdUrl(emp.id), { is_active: 1, end_date: null });
      toastSuccess('Employee reactivated');
      load();
    } catch (err) { showAxiosError(err); }
  };

  const departments = [...new Set(employees.map((e) => e.department).filter(Boolean))].sort();

  const filtered = employees.filter((e) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      e.full_name?.toLowerCase().includes(q) ||
      e.employee_no?.toLowerCase().includes(q) ||
      e.department?.toLowerCase().includes(q) ||
      e.position?.toLowerCase().includes(q) ||
      e.phone?.includes(q)
    );
  });

  const formFooter = (
    <>
      <button className="btn-ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
      <div className="grow" />
      <button className="btn-primary" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : editId ? 'Save changes' : 'Register employee'}
      </button>
    </>
  );

  return (
    <>
      <div className="kpi-row">
        <KpiCard label="Active employees" val={employees.filter((e) =>  e.is_active).length} hint="currently on staff"    tone="brand" />
        <KpiCard label="Departments"       val={departments.length}                            hint="distinct"              tone="accent" />
        <KpiCard label="Biometric enrolled" val={employees.filter((e) => e.device_user_id && e.is_active).length} hint="fingerprint linked" tone="success" />
        <KpiCard label="Inactive"           val={employees.filter((e) => !e.is_active).length} hint="deactivated"          tone="warn" />
      </div>

      <div className="panel">
        <div className="panel-head">
          <div className="head-text">
            <h2>Employees</h2>
            <span className="sub">
              Register staff members and link them to their fingerprint terminal ID.
              Once linked, attendance scans resolve to their name automatically.
            </span>
          </div>
          <div className="head-actions">
            <input
              className="f-input" placeholder="Search…" value={search}
              onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 200 }}
            />
            <select className="f-select" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} style={{ maxWidth: 180 }}>
              <option value="">All departments</option>
              {departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select className="f-select" value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)} style={{ maxWidth: 130 }}>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
              <option value="">All</option>
            </select>
            {canManage && <button className="btn-primary" onClick={openNew}>+ Add Employee</button>}
          </div>
        </div>

        {loading ? (
          <div className="empty-state"><div className="ic">⏳</div><h4>Loading…</h4></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="ic">👥</div>
            <h4>{employees.length === 0 ? 'No employees registered yet' : 'No results'}</h4>
            {employees.length === 0 && canManage && (
              <p>Click <strong>Add Employee</strong> to register your first staff member. Set their fingerprint terminal ID so attendance scans automatically show their name.</p>
            )}
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Dept / Position</th>
                  <th>Phone</th>
                  <th>Branch</th>
                  <th>Hire date</th>
                  <th>Biometric ID</th>
                  <th>Status</th>
                  {canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {filtered.map((emp) => (
                  <tr key={emp.id} style={!emp.is_active ? { opacity: 0.55 } : {}}>
                    <td>
                      <div className="strong">{emp.full_name}</div>
                      {emp.employee_no && <div className="muted" style={{ fontSize: 11 }}>#{emp.employee_no}</div>}
                    </td>
                    <td>
                      {emp.department && <div style={{ fontSize: 13 }}>{emp.department}</div>}
                      {emp.position   && <div className="muted" style={{ fontSize: 12 }}>{emp.position}</div>}
                    </td>
                    <td className="muted">{emp.phone || '—'}</td>
                    <td className="muted">{emp.branch_name || '—'}</td>
                    <td className="muted">{emp.hire_date ? fmtDate(emp.hire_date) : '—'}</td>
                    <td>
                      {emp.device_user_id
                        ? <span className="badge badge-green" style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>ID {emp.device_user_id}</span>
                        : <span className="badge badge-gold no-dot" style={{ fontSize: 11 }}>Not enrolled</span>}
                    </td>
                    <td>
                      {emp.is_active
                        ? <span className="badge badge-green">Active</span>
                        : <span className="badge badge-red no-dot">Inactive</span>}
                    </td>
                    {canManage && (
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn-ghost btn-sm" onClick={() => openEdit(emp)}>Edit</button>
                          {emp.is_active
                            ? <button className="btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => deactivate(emp)}>Deactivate</button>
                            : <button className="btn-ghost btn-sm" onClick={() => reactivate(emp)}>Reactivate</button>}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        title={editId ? 'Edit Employee' : 'Register Employee'}
        open={open}
        onClose={() => setOpen(false)}
        footer={formFooter}
        width={680}
      >
        {/* Personal */}
        <p style={{ margin: '0 0 10px', fontWeight: 600, color: 'var(--ink)', fontSize: 13 }}>Personal Information</p>
        <div className="f-grid f-grid-2">
          <div className="f-row">
            <label className="f-label">Full name *</label>
            <input className="f-input" value={form.full_name} onChange={f('full_name')} placeholder="e.g. Kwame Asante" />
          </div>
          <div className="f-row">
            <label className="f-label">Employee no.</label>
            <input className="f-input" value={form.employee_no} onChange={f('employee_no')} placeholder="e.g. EMP-001" />
          </div>
          <div className="f-row">
            <label className="f-label">Gender</label>
            <select className="f-select" value={form.gender} onChange={f('gender')}>
              <option value="">— Select —</option>
              <option>Male</option><option>Female</option><option>Other</option>
            </select>
          </div>
          <div className="f-row">
            <label className="f-label">Date of birth</label>
            <input type="date" className="f-input" value={form.date_of_birth} onChange={f('date_of_birth')} />
          </div>
          <div className="f-row">
            <label className="f-label">Ghana Card / National ID</label>
            <input className="f-input" value={form.national_id} onChange={f('national_id')} placeholder="GHA-XXXXXXXXX-X" />
          </div>
          <div className="f-row">
            <label className="f-label">Phone</label>
            <input className="f-input" value={form.phone} onChange={f('phone')} placeholder="024XXXXXXX" />
          </div>
          <div className="f-row">
            <label className="f-label">Email</label>
            <input type="email" className="f-input" value={form.email} onChange={f('email')} />
          </div>
          <div className="f-row">
            <label className="f-label">Address</label>
            <input className="f-input" value={form.address} onChange={f('address')} />
          </div>
        </div>

        {/* Employment */}
        <p style={{ margin: '18px 0 10px', fontWeight: 600, color: 'var(--ink)', fontSize: 13 }}>Employment Details</p>
        <div className="f-grid f-grid-2">
          <div className="f-row">
            <label className="f-label">Department</label>
            <input className="f-input" value={form.department} onChange={f('department')} placeholder="e.g. Pharmacy, Cashier, Warehouse" />
          </div>
          <div className="f-row">
            <label className="f-label">Position / Job title</label>
            <input className="f-input" value={form.position} onChange={f('position')} placeholder="e.g. Pharmacist, Cashier" />
          </div>
          <div className="f-row">
            <label className="f-label">Employment type</label>
            <select className="f-select" value={form.employment_type} onChange={f('employment_type')}>
              {EMP_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="f-row">
            <label className="f-label">Branch</label>
            <select className="f-select" value={form.branch_id} onChange={f('branch_id')}>
              <option value="">— Select branch —</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div className="f-row">
            <label className="f-label">Hire date</label>
            <input type="date" className="f-input" value={form.hire_date} onChange={f('hire_date')} />
          </div>
          <div className="f-row">
            <label className="f-label">End date <span className="muted" style={{ fontWeight: 400 }}>(blank if still active)</span></label>
            <input type="date" className="f-input" value={form.end_date} onChange={f('end_date')} />
          </div>
        </div>

        {/* Biometric */}
        <p style={{ margin: '18px 0 6px', fontWeight: 600, color: 'var(--ink)', fontSize: 13 }}>Fingerprint / Biometric Link</p>
        <p className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
          Enroll the employee on the ZKTeco terminal and note the numeric ID assigned to them. Enter it below so all future scans automatically show their name.
        </p>
        <div className="f-grid f-grid-2">
          <div className="f-row">
            <label className="f-label">Biometric ID (from terminal)</label>
            <input
              className="f-input" value={form.device_user_id} onChange={f('device_user_id')}
              placeholder="e.g. 5" style={{ fontFamily: 'var(--font-mono)' }}
            />
          </div>
          <div className="f-row">
            <label className="f-label">Fingerprint terminal</label>
            <select className="f-select" value={form.device_serial} onChange={f('device_serial')}>
              <option value="">— Select terminal —</option>
              {devices.map((d) => (
                <option key={d.id} value={d.serial_number}>
                  {d.name || d.serial_number}{d.branch_name ? ` (${d.branch_name})` : ''}
                </option>
              ))}
              {devices.length === 0 && <option disabled>No terminals registered yet</option>}
            </select>
          </div>
        </div>
      </Modal>
    </>
  );
};

const KpiCard = ({ label, val, hint, tone }) => (
  <div className="stat">
    <div className="lbl">{label}</div>
    <div className="val">{val}</div>
    <div className="hint">{hint}</div>
    <div className="ic" style={
      tone === 'brand'   ? { background: 'var(--brand-soft)',   color: 'var(--brand)' }
    : tone === 'accent'  ? { background: 'var(--accent-soft)',  color: 'var(--accent)' }
    : tone === 'warn'    ? { background: 'var(--warn-soft)',    color: 'var(--warn)' }
    : tone === 'success' ? { background: 'var(--success-soft)', color: 'var(--success)' }
    : {}}>👥</div>
  </div>
);

export default HREmployees;
